import React from 'react';
import { AlertTriangle, CheckCircle2, Clock, Zap } from 'lucide-react';
import { MILESTONE_STATUS_COLORS } from '../theme/decisionTokens';

export interface MilestoneRowModel {
  title: string;
  phase: string;
  owner: string;
  due: string;
  status: string;
  pct: number;
  blocker?: string;
}

export interface MilestoneRowProps {
  milestone: MilestoneRowModel;
}

const STATUS_ICON: Record<string, React.ElementType> = {
  Complete: CheckCircle2,
  'In Progress': Zap,
  'Not Started': Clock,
  'At Risk': AlertTriangle,
  Delayed: AlertTriangle,
};

export const MilestoneRow: React.FC<MilestoneRowProps> = ({ milestone }) => {
  const color = MILESTONE_STATUS_COLORS[milestone.status] ?? MILESTONE_STATUS_COLORS['Not Started'];
  const Icon = STATUS_ICON[milestone.status] ?? Clock;
  const inFlight = milestone.pct > 0 && milestone.pct < 100;

  return (
    <div className="flex items-start gap-3 bg-[#F5F7FA] rounded-lg border border-[#e5e7eb] px-3.5 py-3">
      <Icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-bold text-[#18365E]">{milestone.title}</span>
          {milestone.phase && (
            <span className="text-[9px] font-bold uppercase tracking-wide text-[#717182] bg-white border border-[#e5e7eb] px-1.5 py-0.5 rounded">
              {milestone.phase}
            </span>
          )}
        </div>
        <p className="text-[11px] text-[#717182] mt-0.5">
          {milestone.owner} · due {milestone.due}
          {milestone.status === 'In Progress' && ` · ${milestone.pct}% complete`}
        </p>
        {inFlight && (
          <div className="h-1.5 bg-white rounded-full mt-1.5 border border-[#e5e7eb] overflow-hidden">
            <div
              className="h-full bg-[#2563eb] rounded-full transition-all duration-300"
              style={{ width: `${milestone.pct}%` }}
            />
          </div>
        )}
        {milestone.blocker && (
          <p className="text-[11px] text-amber-700 mt-1 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3 shrink-0" />
            {milestone.blocker}
          </p>
        )}
      </div>
      <span className="text-[10px] font-semibold shrink-0" style={{ color }}>
        {milestone.status}
      </span>
    </div>
  );
};

export default MilestoneRow;
